import * as React from 'react';
import {
  FILTER_CRITERIA,
  filterValues,
  isEmptyFilter,
  withFilterValues,
  type ChoiceControl,
  type FilterControl,
  type FilterCriterion,
  type FilterKey,
  type TextControl,
} from '@chess/api-games/filter';
import { type GameFilter } from '@chess/api-model';

// The library's filter bar, drawn from props only. Its controls come from the
// filter's registry of criteria (`FILTER_CRITERIA`): each criterion says how
// it is offered (a menu of checkboxes or a search field), where it sits, and
// whether it waits under "More filters", so a new criterion appears here
// without a change to this file. Under the controls, a chip per active value
// removes it, and "Clear all" removes everything. `index.tsx` reads and
// writes the filter in the library's URL and supplies the counts and
// suggestions; stories pass them by hand.

/** Per option: how many games the filter would show with that option alone chosen for its criterion. */
export type OptionCounts = Partial<Record<FilterKey, Readonly<Record<string, number>>>>;

export interface FilterBarProps {
  filter: GameFilter;
  /** How many games the filter shows, when known. */
  count?: number;
  /** Each choice's count, shown beside it in its menu; none while unknown. */
  counts?: OptionCounts;
  /** Per search field, values to suggest while typing. */
  suggestions?: Partial<Record<FilterKey, readonly string[]>>;
  onChange: (filter: GameFilter) => void;
  /** Stories: a menu open from the start. */
  initialMenu?: FilterKey;
}

type Criterion = FilterCriterion<FilterKey>;
type Offered = { criterion: Criterion; control: FilterControl<string> };
type Choice = Offered & { control: ChoiceControl<string> & { order: number; more?: boolean } };
type Search = Offered & { control: TextControl & { order: number; more?: boolean } };

/** The criteria the bar offers, in their order. */
const OFFERED: Offered[] = FILTER_CRITERIA.filter((c) => c.control)
  .map((c) => ({ criterion: c as Criterion, control: c.control as unknown as FilterControl<string> }))
  .sort((a, b) => a.control.order - b.control.order);
const isChoice = (o: Offered): o is Choice => o.control.kind === 'choice';
const isSearch = (o: Offered): o is Search => o.control.kind === 'text';

/** A choice's words: its label, after its group's name when it has one ("Middlegame: miss"). */
function optionLabel(control: ChoiceControl<string>, value: string): string {
  const option = control.options.find(([v]) => v === value);
  if (!option) return control.linkedLabel?.(value) ?? value;
  const [, label, group] = option;
  return group ? `${group}: ${label.charAt(0).toLowerCase()}${label.slice(1)}` : label;
}

const searchValue = ({ criterion, control }: Search, filter: GameFilter): string | undefined =>
  control.read ? control.read(filter) : filterValues(filter, criterion)[0];
const writeSearch = ({ criterion, control }: Search, filter: GameFilter, value: string | undefined): GameFilter =>
  control.write ? control.write(filter, value) : withFilterValues(filter, criterion, value ? [value] : []);

const Caret = () => (
  <svg className="filter-caret" viewBox="0 0 10 6" aria-hidden="true">
    <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Cross = () => (
  <svg className="filter-cross" viewBox="0 0 10 10" aria-hidden="true">
    <path d="M2 2l6 6M8 2l-6 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/** One criterion's button and, while open, its menu of checkboxes with counts. */
function ChoiceMenu({ offered, filter, counts, open, onOpen, onClose, onChange }: {
  offered: Choice;
  filter: GameFilter;
  counts?: Readonly<Record<string, number>>;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onChange: (filter: GameFilter) => void;
}) {
  const { criterion, control } = offered;
  const chosen = filterValues(filter, criterion);
  const anchor = React.useRef<HTMLDivElement>(null);
  const button = React.useRef<HTMLButtonElement>(null);
  const menuId = React.useId();

  // Closes on a click outside or Escape, giving the focus back to the button.
  React.useEffect(() => {
    if (!open) return;
    const outside = (e: MouseEvent) => {
      if (anchor.current && !anchor.current.contains(e.target as Node)) onClose();
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onClose();
      button.current?.focus();
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    anchor.current?.querySelector<HTMLInputElement>('input')?.focus();
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (value: string) =>
    onChange(withFilterValues(filter, criterion, chosen.includes(value) ? chosen.filter((v) => v !== value) : [...chosen, value]));

  // The options under their groups, then any value only a link reached.
  const groups: Array<{ name?: string; values: Array<[string, string]> }> = [];
  for (const [value, label, group] of control.options) {
    const last = groups[groups.length - 1];
    if (last && last.name === group) last.values.push([value, label]);
    else groups.push({ name: group, values: [[value, label]] });
  }
  const linked = chosen.filter((v) => !control.options.some(([o]) => o === v));
  if (linked.length) groups.push({ name: 'From a link', values: linked.map((v) => [v, control.linkedLabel?.(v) ?? v]) });

  const summary = chosen.length === 0 ? undefined : chosen.length <= 2 ? chosen.map((v) => optionLabel(control, v)).join(', ') : `${optionLabel(control, chosen[0])} +${chosen.length - 1}`;

  return (
    <div className="filter-menu-anchor" ref={anchor}>
      <button
        ref={button}
        type="button"
        className={`filter-button${chosen.length ? ' filter-button-on' : ''}`}
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? onClose() : onOpen())}>
        <span>{control.label}</span>
        {summary && <span className="filter-button-value">{summary}</span>}
        <Caret />
      </button>
      {open && (
        <div className="filter-menu" id={menuId} role="group" aria-label={control.label}>
          {groups.map((g) => (
            <div key={g.name ?? ''} className="filter-menu-group">
              {g.name && <div className="filter-menu-heading">{g.name}</div>}
              {g.values.map(([value, label]) => {
                const on = chosen.includes(value);
                const n = counts?.[value];
                return (
                  <label key={value} className={`filter-menu-option${!on && n === 0 ? ' filter-menu-option-empty' : ''}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(value)} />
                    <span className="filter-menu-label">{label}</span>
                    {n !== undefined && <span className="filter-menu-count">{n}</span>}
                  </label>
                );
              })}
            </div>
          ))}
          {chosen.length > 0 && (
            <button type="button" className="btn btn-ghost filter-menu-clear" onClick={() => onChange(withFilterValues(filter, criterion, []))}>
              Clear {control.label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** A search field that writes after a short pause, suggests values, and follows the URL when it changes elsewhere. */
function SearchField({ offered, filter, suggestions, onChange }: { offered: Search; filter: GameFilter; suggestions?: readonly string[]; onChange: (filter: GameFilter) => void }) {
  const value = searchValue(offered, filter);
  const [text, setText] = React.useState(value ?? '');
  const listId = React.useId();
  React.useEffect(() => setText(value ?? ''), [value]);
  React.useEffect(() => {
    if ((value ?? '') === text.trim()) return;
    const timer = setTimeout(() => onChange(writeSearch(offered, filter, text.trim() || undefined)), 300);
    return () => clearTimeout(timer);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <label className="filter-search">
      <span className="filter-visually-hidden">{offered.control.label}</span>
      <input
        className="input filter-input"
        type="search"
        value={text}
        placeholder={offered.control.placeholder}
        list={suggestions?.length ? listId : undefined}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setText('');
        }}
      />
      {suggestions?.length ? (
        <datalist id={listId}>
          {suggestions.slice(0, 50).map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      ) : null}
    </label>
  );
}

/** A chip per active value, each removing its value, and "Clear all". */
function ActiveChips({ filter, onChange }: { filter: GameFilter; onChange: (filter: GameFilter) => void }) {
  const chips: Array<{ key: string; label: string; value: string; remove: () => void }> = [];
  for (const offered of OFFERED) {
    if (isChoice(offered)) {
      const chosen = filterValues(filter, offered.criterion);
      for (const v of chosen) {
        chips.push({
          key: `${offered.criterion.key}:${v}`,
          label: offered.control.label,
          value: optionLabel(offered.control, v),
          remove: () => onChange(withFilterValues(filter, offered.criterion, chosen.filter((c) => c !== v))),
        });
      }
    } else if (isSearch(offered)) {
      const v = searchValue(offered, filter);
      if (v) chips.push({ key: offered.criterion.key, label: offered.control.label, value: v, remove: () => onChange(writeSearch(offered, filter, undefined)) });
    }
  }
  if (!chips.length) return null;
  return (
    <div className="filter-chips" aria-label="Active filters">
      {chips.map((c) => (
        <span key={c.key} className="filter-chip">
          <span className="filter-chip-label">{c.label}</span>
          <span className="filter-chip-value">{c.value}</span>
          <button type="button" className="filter-chip-remove" aria-label={`Remove ${c.label}: ${c.value}`} onClick={c.remove}>
            <Cross />
          </button>
        </span>
      ))}
      <button type="button" className="btn btn-ghost filter-clear" onClick={() => onChange({})}>
        Clear all
      </button>
    </div>
  );
}

export const FilterBar: React.FC<FilterBarProps> = ({ filter, count, counts, suggestions, onChange, initialMenu }) => {
  const [menu, setMenu] = React.useState<FilterKey | undefined>(initialMenu);
  const [more, setMore] = React.useState(false);
  const any = !isEmptyFilter({ ...filter, ids: undefined });
  const isSet = (o: Offered) => filterValues(filter, o.criterion).length > 0 || (isSearch(o) && !!searchValue(o, filter));
  // A filter under "More filters" stays in view while it is set.
  const shown = (o: Offered) => !o.control.more || more || isSet(o);
  const hidden = OFFERED.filter((o) => o.control.more && !shown(o)).length;
  const offersMore = OFFERED.some((o) => o.control.more);

  return (
    <div className="library-filter" role="search" aria-label="Filter games">
      <div className="filter-row">
        {OFFERED.filter(isChoice)
          .filter(shown)
          .map((o) => (
            <ChoiceMenu
              key={o.criterion.key}
              offered={o}
              filter={filter}
              counts={counts?.[o.criterion.key]}
              open={menu === o.criterion.key}
              onOpen={() => setMenu(o.criterion.key)}
              onClose={() => setMenu((m) => (m === o.criterion.key ? undefined : m))}
              onChange={onChange}
            />
          ))}
        {offersMore && (hidden > 0 || more) && (
          <button type="button" className="btn btn-ghost filter-more" aria-expanded={more} onClick={() => setMore(!more)}>
            {more ? 'Fewer filters' : 'More filters'}
          </button>
        )}
        {OFFERED.filter(isSearch)
          .filter(shown)
          .map((o) => (
            <SearchField key={o.criterion.key} offered={o} filter={filter} suggestions={suggestions?.[o.criterion.key]} onChange={onChange} />
          ))}
        {count !== undefined && any && <span className="filter-count">{count === 1 ? '1 game' : `${count} games`}</span>}
      </div>
      <ActiveChips filter={filter} onChange={onChange} />
    </div>
  );
};
